import {Inject, Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {Observable} from 'rxjs';
import {ServerEndpoints} from '../../../server.endpoints';
import {
  ConfigurationOverview,
  DeviceConfigurationResource,
  DeviceListItem,
  DeviceSettingDefinition,
  EffectiveConfiguration,
  SettingsDocument
} from '../shared/models/device-config.model';

/**
 * Thin HTTP client for the DeviceService configuration API
 * (`/api/devices/v2026.6/configuration`). Requests go through the gateway under the SPA's single `BASE_URL`
 * origin, so the auth interceptor attaches the context token automatically (mirrors {@link FormHttpService}).
 */
@Injectable({providedIn: 'root'})
export class DeviceConfigHttpService {
  constructor(private http: HttpClient, @Inject('BASE_URL') private baseUrl: string) {
  }

  private get base(): string {
    return this.baseUrl + ServerEndpoints.DeviceConfig__v2026_6;
  }

  /** The setting catalog that drives the editor. */
  catalog(): Observable<DeviceSettingDefinition[]> {
    return this.http.get<DeviceSettingDefinition[]>(`${this.base}/catalog`);
  }

  /** The registered devices the caller may target for an override. */
  devices(): Observable<DeviceListItem[]> {
    return this.http.get<DeviceListItem[]>(`${this.base}/devices`);
  }

  /** Company baseline (no shardId) or a shard baseline. */
  getBaseline(shardId?: string): Observable<DeviceConfigurationResource> {
    return this.http.get<DeviceConfigurationResource>(`${this.base}/baseline`,
      shardId ? {params: {shardId}} : {});
  }

  putBaseline(settings: SettingsDocument, shardId?: string): Observable<DeviceConfigurationResource> {
    return this.http.put<DeviceConfigurationResource>(`${this.base}/baseline`, settings,
      shardId ? {params: {shardId}} : {});
  }

  /** Per-device override layer. */
  getOverride(clientId: string): Observable<DeviceConfigurationResource> {
    return this.http.get<DeviceConfigurationResource>(`${this.base}/override/${clientId}`);
  }

  putOverride(clientId: string, settings: SettingsDocument): Observable<DeviceConfigurationResource> {
    return this.http.put<DeviceConfigurationResource>(`${this.base}/override/${clientId}`, settings);
  }

  /** The fully-merged effective configuration for a device (admin preview). */
  effective(clientId: string): Observable<EffectiveConfiguration> {
    return this.http.get<EffectiveConfiguration>(`${this.base}/effective/${clientId}`);
  }

  /**
   * The merged configuration of a **shard** — company ← shard, with **no device layer** (#205).
   *
   * The web screens that have to agree with the register (the Offene-Tische overview's idle-warning thresholds and
   * colours) are not looking at any one device: they are looking at what the shard as a whole is configured to do.
   * `effective/{clientId}` cannot answer that — it always folds in one device's overrides, so the answer would
   * differ depending on which register happened to be picked.
   *
   * @param shardId Optional: preview another shard of the caller's own company. Omitted, the shard is resolved from
   *   the caller's context token, which is the normal case (and the only one an Operator can use).
   */
  effectiveForShard(shardId?: string): Observable<EffectiveConfiguration> {
    return this.http.get<EffectiveConfiguration>(`${this.base}/effective/shard`,
      shardId ? {params: {shardId}} : {});
  }

  /** Read-only summary of which shards/devices diverge from the inherited configuration. */
  overview(): Observable<ConfigurationOverview> {
    return this.http.get<ConfigurationOverview>(`${this.base}/overview`);
  }
}
